import { marked } from 'marked';
import { useCallback, useState } from 'react';
import { isNullish } from 'remeda';
import { useCurrentUserProfile } from 'src/graphql';
import { useAiMessageComposition } from 'src/graphql/entities/ai-message-composition';

export const useAiAssistant = ({
  setContent,
  conversationId,
  conversationPartnerId,
}: {
  setContent?: (value: string) => void;
  conversationId: string;
  conversationPartnerId: number;
}) => {
  const [isGeneratingAiSuggestion, setIsGeneratingAiSuggestion] = useState(false);
  const [pendingAiSuggestion, setPendingAiSuggestion] = useState('');
  const [currentUserInput, setCurrentUserInput] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [previousResponseId, setPreviousResponseId] = useState('');
  const [selectedPromptName, setSelectedPromptName] = useState('');
  const [conversationMessageId, setConversationMessageId] = useState<string | undefined>();
  const { getAiMessageComposition } = useAiMessageComposition();
  const { user } = useCurrentUserProfile();

  const handleIncomingAiSuggestion = useCallback(
    async (message: string, responseId?: string) => {
      setIsGeneratingAiSuggestion(false);

      if (message && setContent) {
        setContent(await marked.parse(message || ''));
        setPendingAiSuggestion(message);

        if (responseId && responseId !== previousResponseId) {
          setPreviousResponseId(responseId || '');
        }
      }
    },
    [previousResponseId, setContent],
  );

  const handleGenerationStarted = useCallback(
    (opts?: {
      promptId?: string;
      promptName?: string;
      customPrompt?: string;
      userInput?: string;
    }) => {
      const { promptId, promptName, customPrompt, userInput } = opts || {};
      setCustomPrompt(customPrompt || '');
      setSelectedPrompt(promptId || '');
      setSelectedPromptName(promptName || '');
      setCurrentUserInput(userInput || '');
      setIsGeneratingAiSuggestion(true);
      setPendingAiSuggestion('');
    },
    [],
  );

  const getInitialSuggestion = useCallback(
    async ({
      promptId,
      promptName,
      customPrompt,
      userId,
      userInput,
    }: {
      promptId?: string;
      promptName?: string;
      customPrompt?: string;
      userId?: string;
      userInput?: string;
    }): Promise<void> => {
      if (isNullish(userId)) return;

      const generatedConversationMessageId = crypto.randomUUID();
      setConversationMessageId(generatedConversationMessageId);
      handleGenerationStarted({ promptId, promptName, customPrompt, userInput });

      const newSuggestion = await getAiMessageComposition({
        partnerId: conversationPartnerId,
        userId,
        conversationId,
        conversationMessageId: generatedConversationMessageId,
        promptId,
        customPrompt,
        userInput,
      });

      const { responseId: newResponseId, aiMessage } =
        newSuggestion?.data?.getAiMessageComposition || {};

      await handleIncomingAiSuggestion(aiMessage || '', newResponseId);
    },
    [
      conversationId,
      conversationPartnerId,
      getAiMessageComposition,
      handleGenerationStarted,
      handleIncomingAiSuggestion,
      setConversationMessageId,
    ],
  );

  const handleRefineSuggestion = useCallback(
    async (correction: string) => {
      if (!correction || isNullish(user?.id)) return;

      setIsGeneratingAiSuggestion(true);
      const ensuredConversationMessageId = conversationMessageId ?? crypto.randomUUID();
      if (!conversationMessageId) {
        setConversationMessageId(ensuredConversationMessageId);
      }
      const newSuggestion = await getAiMessageComposition({
        partnerId: conversationPartnerId,
        userId: user?.id,
        conversationId,
        userInput: pendingAiSuggestion,
        conversationMessageId: ensuredConversationMessageId,
        correction,
        customPrompt,
        promptId: selectedPrompt,
        previousResponseId,
      });

      const { responseId: newResponseId, aiMessage } =
        newSuggestion?.data?.getAiMessageComposition || {};

      await handleIncomingAiSuggestion(aiMessage || '', newResponseId);
    },
    [
      conversationId,
      conversationPartnerId,
      customPrompt,
      conversationMessageId,
      getAiMessageComposition,
      handleIncomingAiSuggestion,
      pendingAiSuggestion,
      previousResponseId,
      selectedPrompt,
      setConversationMessageId,
      user?.id,
    ],
  );

  const resetAiAssistant = useCallback(
    ({ preserveMessageId }: { preserveMessageId?: boolean } = {}) => {
      setIsGeneratingAiSuggestion(false);
      setPendingAiSuggestion('');
      setCurrentUserInput('');
      setSelectedPrompt('');
      setCustomPrompt('');
      setPreviousResponseId('');
      setSelectedPromptName('');
      if (!preserveMessageId) {
        setConversationMessageId(undefined);
      }
    }, [setConversationMessageId]);

  const handleDismissAiSuggestion = useCallback(async () => {
    if (!setContent) return;
    setContent(await marked.parse(currentUserInput || ''));
    resetAiAssistant();
  }, [currentUserInput, resetAiAssistant, setContent]);

  const handleAcceptAiSuggestion = useCallback(() => {
    setCurrentUserInput(pendingAiSuggestion);
    resetAiAssistant({ preserveMessageId: true });
  }, [pendingAiSuggestion, resetAiAssistant]);

  return {
    isGeneratingAiSuggestion,
    pendingAiSuggestion,
    currentUserInput,
    selectedPrompt,
    customPrompt,
    handleRefineSuggestion,
    handleDismissAiSuggestion,
    handleAcceptAiSuggestion,
    resetAiAssistant,
    getInitialSuggestion,
    conversationMessageId,
    selectedPromptName: selectedPromptName || customPrompt || '',
  };
};
